import { graphql } from "lib/generated/app-service-gql";
import {
  FeedPreviewNoticesQuery,
  FeedPreviewNoticesRequest,
} from "lib/generated/app-service-gql/graphql";
import { SelfServeQuery, SignalCategory } from "lib/StotlesApi";
import { RecordPreview, TenderStage } from "lib/types/models";
import { SIGNAL_PREVIEW_CATEGORY } from "./signals/useSignalPreviewCounts";
import { useGraphQlQuery } from "./useGraphQlClient";

const feedPreviewNoticesQuery = graphql(`
  query feedPreviewNotices($request: FeedPreviewNoticesRequest!) {
    feedPreviewNotices(FeedPreviewNoticesRequest: $request) {
      totalResults
      items {
        notice {
          guid
          name
          description
          publishDate
          stage
          procurementStage {
            id
          }
          buyers {
            id
            guid
            name
          }
        }
        matchedSignals {
          key
          category
          name
        }
      }
    }
  }
`);

export const DEFAULT_PREVIEW_LOOKBACK_MONTHS = 6;

type PreviewNotice = FeedPreviewNoticesQuery["feedPreviewNotices"]["items"][number]["notice"];

// A signal that matched a notice, ready to render as a highlight chip.
export type PreviewMatchedSignal = { key: string; category: SignalCategory; name: string };

// A preview notice card + the signals it matched (chips). The procurement stage
// id is kept so the detail popover can lazily fetch keyword highlighting.
export type FeedPreviewRecord = {
  record: RecordPreview;
  procurementStageId: string;
  matchedSignals: PreviewMatchedSignal[];
};

/**
 * Draft signal criteria → the flat item list the preview query takes. Keys match
 * the value so counts and matches line up, mirroring useSignalPreviewCounts.
 */
function toPreviewSignals(feedCriteria: SelfServeQuery): FeedPreviewNoticesRequest["signals"] {
  const keywords = (feedCriteria.keywords?.keywords ?? []).map((keyword) => ({
    key: keyword,
    category: SIGNAL_PREVIEW_CATEGORY.KEYWORD,
    value: keyword,
  }));
  const competitors = (feedCriteria.competitors?.org_ids ?? []).map((orgId) => ({
    key: orgId,
    category: SIGNAL_PREVIEW_CATEGORY.COMPETITOR,
    value: orgId,
  }));
  const partners = (feedCriteria.partners?.org_ids ?? []).map((orgId) => ({
    key: orgId,
    category: SIGNAL_PREVIEW_CATEGORY.PARTNER,
    value: orgId,
  }));
  const cpvCodes = (feedCriteria.cpv_codes?.codes ?? []).map((code) => ({
    key: code,
    category: SIGNAL_PREVIEW_CATEGORY.CPV_CODE,
    value: code,
  }));

  return [...keywords, ...competitors, ...partners, ...cpvCodes];
}

// Maps a preview notice from the GraphQL response onto the RecordPreview shape
// the card list renders.
function toRecordPreview(notice: PreviewNotice): RecordPreview {
  const buyer = notice.buyers[0];
  return {
    guid: notice.guid,
    name: notice.name ?? "",
    description: notice.description ?? null,
    publish_date: notice.publishDate,
    buyer: buyer
      ? { id: buyer.id, guid: buyer.guid, name: buyer.name }
      : { id: 0, guid: "", name: "" },
    // The generated stage enum shares TenderStage's string values; normalise
    // via String() to convert between the two identical enums.
    stage: String(notice.stage ?? TenderStage.OPEN) as TenderStage,
  };
}

/**
 * Feed-preview notice list served by app-service GraphQL (replaces the legacy
 * Rails record_queries#preview path). Each returned notice carries the signals
 * it matched, so the caller renders highlight chips without a separate lookup.
 *
 * `companyId` previews another company's feed (admin only, enforced server-side).
 */
export function useFeedPreviewNotices(
  feedCriteria: SelfServeQuery,
  companyId?: number,
  options?: { enabled?: boolean },
) {
  const signals = toPreviewSignals(feedCriteria);
  const request: FeedPreviewNoticesRequest = {
    signals,
    companyId,
    options: {
      publishedWithinMonths: DEFAULT_PREVIEW_LOOKBACK_MONTHS,
      countries: feedCriteria.countries,
      languages: feedCriteria.languages,
    },
  };

  const query = useGraphQlQuery(
    ["feedPreviewNotices", request],
    feedPreviewNoticesQuery,
    [{ request }],
    {
      keepPreviousData: true,
      enabled: (options?.enabled ?? true) && signals.length > 0,
      retry: false,
    },
  );

  const data = query.data?.feedPreviewNotices;

  const previewRecords: FeedPreviewRecord[] = (data?.items ?? []).map(
    ({ notice, matchedSignals }) => ({
      record: toRecordPreview(notice),
      procurementStageId: notice.procurementStage.id,
      matchedSignals: matchedSignals.map((signal) => ({
        key: signal.key,
        category: signal.category as SignalCategory,
        name: signal.name,
      })),
    }),
  );

  return { previewRecords, totalResults: data?.totalResults ?? 0, ...query };
}
